import { useGetHomeDetailsQuery } from '@/redux';
import { BaselineFloorplanPanel } from './Floorplan/BaselineFloorplanPanel';
import { useMemo, useState } from 'react';
import { isEmpty, orderBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Alert } from 'antd';
import { HomeBaselineFilterPanel } from './HomeBaselineFilterPanel';
import { BaselinePanel } from './BaselinePanel';
import { DndProviderControlProps } from '@/components';

export interface HomeBaselinePanelProps extends DndProviderControlProps {
  homeId: string;
  filterClassName?: string;
  floorplanImageClassName?: string;
}

function useHomeBaseline(homeId: string) {
  const { data, isFetching } = useGetHomeDetailsQuery({ id: homeId });
  const [selectedProjectIds, setSelectedProjectIds] = useState<
    string[] | null
  >();

  return useMemo(() => {
    const finalized = data?.personalizationHistory?.filter(
      (x) => x.finalizedAt,
    );
    const last = finalized
      ? orderBy(finalized, (x) => x.finalizedAt, 'desc')[0]
      : null;
    return {
      selectedProjectIds,
      setSelectedProjectIds,
      lastFinalizedProjectId: last?.projectId,
      floorplanId: data?.floorplan?.id,
      isFetching,
      finalizedProjectIds: finalized?.map((x) => x.projectId) ?? [],
    };
  }, [
    data?.floorplan?.id,
    data?.personalizationHistory,
    isFetching,
    selectedProjectIds,
  ]);
}

export function HomeBaselinePanel(props: HomeBaselinePanelProps) {
  const { homeId, filterClassName, floorplanImageClassName, skipProvider } =
    props;
  const { t } = useTranslation();

  const {
    isFetching,
    lastFinalizedProjectId,
    floorplanId,
    finalizedProjectIds,
    selectedProjectIds,
    setSelectedProjectIds,
  } = useHomeBaseline(homeId);

  if (
    isFetching ||
    !floorplanId ||
    !lastFinalizedProjectId ||
    isEmpty(finalizedProjectIds)
  )
    return <Alert type="info" description={t('homes.baseline.empty')} />;

  return (
    <div>
      <HomeBaselineFilterPanel
        projectIds={finalizedProjectIds}
        onSubmit={(value) => {
          setSelectedProjectIds(value.projectIds);
        }}
        className={filterClassName}
      />
      <BaselineFloorplanPanel
        {...props}
        floorplanId={floorplanId}
        initialProjectId={lastFinalizedProjectId}
        projectIds={selectedProjectIds}
        className={floorplanImageClassName}
        skipProvider={skipProvider}
      />
      <BaselinePanel
        {...props}
        projectIds={selectedProjectIds}
        className="mt-4"
      />
    </div>
  );
}
